import { Button, ButtonLevel, ButtonSize, ImpactBolt, Text, TextColor, TextVariant } from '@vizit/atoms';
import { Tooltip, TooltipTrigger } from '@vizit/atoms';
import { InfoCircleIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { MetricCardSkeleton } from '../metric-card-skeleton';
import { containerClasses, getAssetMixColor } from './styles';
import type { AssetMixV2Props } from './types';

/**
 * Asset mix metric card showing how many asset classes are missing
 * compared to the required number. Green when none missing, orange when
 * 1-3 missing, red when 4 or more are missing.
 *
 * @example
 * <AssetMixV2 missing={2} required={7} impactLevel="medium" />
 */
const AssetMixV2 = React.forwardRef<HTMLDivElement, AssetMixV2Props>((props, forwardedRef) => {
  const {
    missing,
    required,
    impactLevel,
    isDisabled = false,
    isFocused = false,
    isLoading = false,
    className,
    ...other
  } = props;

  if (isLoading) {
    return (
      <div
        ref={forwardedRef}
        className={cx(containerClasses({ state: 'default' }), className)}
        aria-busy="true"
        data-testid="asset-mix-v2"
        {...other}
      >
        <MetricCardSkeleton />
      </div>
    );
  }

  const state = isDisabled ? 'disabled' : isFocused ? 'focused' : 'default';

  return (
    <div
      ref={forwardedRef}
      className={cx(containerClasses({ state }), className)}
      data-testid="asset-mix-v2"
      data-state={state}
      {...other}
    >
      <div className="flex items-start justify-between w-full">
        <div className="flex items-center gap-1">
          <Text
            variant={TextVariant.MonoXs}
            color={TextColor.Quaternary}
            className={isDisabled ? 'text-fg-disabled-subtle' : ''}
            showEllipsis={false}
            data-testid="asset-mix-name"
          >
            ASSET MIX
          </Text>
          <TooltipTrigger delay={200}>
            <Button
              aria-label="Asset mix info"
              level={ButtonLevel.Ghost}
              size={ButtonSize.Xs}
              className="cursor-default p-0 h-auto min-h-0"
            >
              <InfoCircleIcon size={12} className={isDisabled ? 'text-fg-disabled-subtle' : 'text-quaternary'} />
            </Button>
            <Tooltip>Match your carousel to include all {required} required asset classes.</Tooltip>
          </TooltipTrigger>
        </div>
        <ImpactBolt level={impactLevel} hideUnfilled isDisabled={isDisabled} />
      </div>

      <div className="flex flex-col gap-0.5">
        <Text
          variant={missing === 0 ? TextVariant.MonoXl : TextVariant.MonoMd}
          elementType="span"
          color={TextColor.Inherit}
          className={getAssetMixColor(missing, isDisabled)}
          showEllipsis={false}
          data-testid="asset-mix-value-calculated"
        >
          {missing === 0 ? required : `${missing} missing`}
        </Text>
        <Text
          variant={TextVariant.MonoSm}
          color={TextColor.Tertiary}
          className={isDisabled ? 'text-fg-disabled-subtle' : ''}
          showEllipsis={false}
          data-testid="asset-mix-value-required"
        >
          of {required} required classes
        </Text>
      </div>
    </div>
  );
});

AssetMixV2.displayName = 'AssetMixV2';

export { AssetMixV2 };
export type { AssetMixV2Props };
